import type {
  ClosingChecklistSection,
  ClosingTaskAttachmentKind,
  ClosingTaskStatus,
  RowStatus,
} from '../generated/enums.js';

export type ClosingPendingCategory =
  'GL_POSTING' | 'BANK_RECONCILIATION' | 'ACCOUNTING_REVIEWS' | 'GL_ACCOUNT_REVIEW';

export interface ClosingCategorySummary {
  count: number;
  totalAmount: string;
}

export interface ClosingPendingSummary {
  glPosting: ClosingCategorySummary;
  bankReconciliation: ClosingCategorySummary;
  accountingReviews: ClosingCategorySummary;
  glAccountReview: ClosingCategorySummary;
}

export interface ClosingPendingSummaryQuery {
  periodStart: string;
  periodEnd: string;
  entityId?: string;
  bookId?: string;
}

/**
 * A contract whose revenue-recognition schedule could not be regenerated and still has not been
 * fixed (ALC-1376). Surfaced in close tracking because an open one means the period being closed
 * may be missing revenue: `code` names the fail-closed guard that tripped (and therefore the
 * remedy), `affectedCompetency` is the oldest month left short, and the occurrence window shows
 * how long it has been that way.
 */
export interface ClosingRevenueScheduleExceptionResponse {
  exceptionId: string;
  contractId: string;
  contractCode: string;
  entityId: string;
  code: string;
  reason: string;
  /** ISO date (YYYY-MM-01) of the competency month at risk. */
  affectedCompetency: string;
  firstOccurredAt: string;
  lastOccurredAt: string;
  occurrenceCount: number;
  /**
   * Set when a person accepted this exception without it being fixed. It STAYS in this list —
   * an acknowledgement is a triage record, not a resolution (only a verified regeneration
   * resolves), so the close still sees the pendency.
   */
  acknowledgedAt: string | null;
  acknowledgedByUserId: string | null;
  acknowledgementNote: string | null;
}

export interface ClosingRevenueScheduleExceptionsQuery {
  entityId?: string;
  /** Only exceptions whose affected competency is at or before this date (YYYY-MM-DD). */
  competencyThrough?: string;
}

export interface AcknowledgeClosingRevenueScheduleExceptionRequest {
  /**
   * Why this exception is being accepted for now — required, it is the audit trail. Acknowledging
   * does NOT close the exception: it remains open and visible to the close until the contract's
   * schedule actually regenerates.
   */
  note: string;
}

export type ClosingPendingSortDirection = 'asc' | 'desc';

export interface ClosingPendingItemsQuery {
  periodStart: string;
  periodEnd: string;
  category: ClosingPendingCategory;
  entityId?: string;
  bookId?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortField?: string;
  sortDirection?: ClosingPendingSortDirection;
}

export interface ClosingPendingBillItem {
  type: 'BILL';
  billId: string;
  vendorName: string | null;
  vendorDocument: string | null;
  documentNumber: string | null;
  documentDescription: string | null;
  competencyDate: string;
  classificationLabel: string | null;
  classificationCode: string | null;
  classificationCount: number;
  retentions: string[];
  grossAmount: string;
  netAmount: string;
  billStatus: string;
}

export interface ClosingPendingPaymentItem {
  type: 'PAYMENT';
  paymentId: string;
  billId: string;
  vendorName: string | null;
  vendorDocument: string | null;
  paidDate: string | null;
  amount: string;
  paymentStatus: string;
}

export interface ClosingPendingBankTxItem {
  type: 'BANK_TX';
  transactionId: string;
  bankAccountName: string | null;
  description: string | null;
  postedAt: string;
  amount: string;
  direction: string;
  transactionType: string;
  reconciliationStatus: string;
}

export interface ClosingPendingCardItem {
  type: 'CARD_ITEM';
  itemId: string;
  cardName: string | null;
  description: string | null;
  postedAt: string;
  amount: string;
  reconciliationStatus: string;
}

export interface ClosingPendingReviewItem {
  type: 'REVIEW';
  reviewRequestId: string;
  entityType: string;
  entityId: string;
  reason: string | null;
  requestType: string;
  billId: string | null;
  vendorName: string | null;
  documentNumber: string | null;
  amount: string | null;
  /**
   * The AP_BILL's competência date (YYYY-MM-DD), or its issue date when competência is unset.
   * `null` for non-AP_BILL rows (tax liability reviews have no competência selection).
   */
  competencyDate: string | null;
  /** True when `competencyDate` fell back to the bill's issue date (ALC-4530). */
  competencyEstimated: boolean;
}

export interface ClosingPendingAccountItem {
  type: 'GL_ACCOUNT';
  glAccountId: string;
  code: string;
  name: string;
  accountType: string;
  naturalBalance: string;
  isPostable: boolean;
  parentAccountCode: string | null;
  parentAccountName: string | null;
  createdAt: string;
  status: string;
}

export type ClosingPendingItem =
  | ClosingPendingBillItem
  | ClosingPendingPaymentItem
  | ClosingPendingBankTxItem
  | ClosingPendingCardItem
  | ClosingPendingReviewItem
  | ClosingPendingAccountItem;

export interface ClosingPendingItemsResponse {
  data: ClosingPendingItem[];
  total: number;
  page: number;
  limit: number;
}

export interface ClosingTaskAttachmentResponse {
  attachmentId: string;
  taskTemplateId: string | null;
  taskId: string | null;
  kind: ClosingTaskAttachmentKind;
  originalName: string;
  mimeType: string;
  fileSizeBytes: string;
  uploadedByUserId: string | null;
  uploadedByUserName: string | null;
  uploadedAt: string;
}

export interface ClosingTaskTemplateResponse {
  taskTemplateId: string;
  title: string;
  description: string;
  dueDayOfMonth: number | null;
  section: ClosingChecklistSection | null;
  responsibleUserId: string | null;
  responsibleUserName: string | null;
  responsibleUserEmail: string | null;
  requireEvidence: boolean;
  status: RowStatus;
  createdByUserId: string | null;
  updatedByUserId: string | null;
  createdAt: string;
  updatedAt: string | null;
  attachments: ClosingTaskAttachmentResponse[];
}

export interface ListClosingTaskTemplatesQuery {
  status?: RowStatus | 'ALL';
  search?: string;
  section?: ClosingChecklistSection;
}

export const ClosingTaskTemplateInstantiationScope = {
  ALL_OPEN_PERIODS: 'ALL_OPEN_PERIODS',
  CURRENT_AND_FUTURE_OPEN_PERIODS: 'CURRENT_AND_FUTURE_OPEN_PERIODS',
} as const;
export type ClosingTaskTemplateInstantiationScope =
  (typeof ClosingTaskTemplateInstantiationScope)[keyof typeof ClosingTaskTemplateInstantiationScope];

export interface ClosingTaskOpenPeriodBackfillSummaryResponse {
  previousOpenPeriodCount: number;
}

export interface CreateClosingTaskTemplateRequest {
  title: string;
  description: string;
  dueDayOfMonth?: number | null;
  section?: ClosingChecklistSection | null;
  responsibleUserId?: string | null;
  requireEvidence?: boolean;
  instantiationScope?: ClosingTaskTemplateInstantiationScope;
}

export interface UpdateClosingTaskTemplateRequest {
  title?: string;
  description?: string;
  dueDayOfMonth?: number | null;
  section?: ClosingChecklistSection | null;
  responsibleUserId?: string | null;
  requireEvidence?: boolean;
  status?: RowStatus;
}

export const EXPENSE_ACCRUAL_OPEN_SYSTEM_SOURCE = 'EXPENSE_ACCRUAL_OPEN' as const;

export interface ExpenseAccrualOpenSystemMetadata {
  version: 1;
  competencyMonth: string;
  /** Every open accrual in the period, payroll-confidential rows included. Authoritative count. */
  openCount: number;
  /** Visibility-filtered — payroll-confidential accruals are omitted, so this may be shorter. */
  accrualIds: string[];
  observedAt: string;
}

/** AP-30: the AI-routed bills of the period still waiting for a provision or for review. */
export const AI_POSTING_REVIEW_SYSTEM_SOURCE = 'AP_AI_POSTING_REVIEW' as const;

export interface AiPostingReviewSystemMetadata {
  version: 1;
  /** Bills approved on the AI path with no provision posted yet. */
  pendingCount: number;
  /** AI journals posted and not yet marked reviewed. */
  unreviewedCount: number;
  /** The review queue filtered to this period's book. */
  actionHref: string;
  observedAt: string;
}

/**
 * The yearly CPC 01 impairment reminder for indefinite-life intangibles (ALC-3489, ADR 0017 D8).
 *
 * CPC 04 §107 does not amortise these — it substitutes an **annual** impairment test, so the
 * only control that makes the substitution real is a task that appears once a year, in the
 * period the fiscal year ends in, while the entity actually holds such an asset.
 */
export const INDEFINITE_LIFE_IMPAIRMENT_SYSTEM_SOURCE = 'INDEFINITE_LIFE_IMPAIRMENT' as const;

export interface IndefiniteLifeImpairmentSystemMetadata {
  version: 1;
  /** The fiscal year this task closes, by the calendar year its last period ends in. */
  fiscalYear: number;
  /** How many in-service assets of an indefinite-life class the entity holds. */
  assetCount: number;
  /** The classes those assets belong to, by code — a taxonomy, never an asset or an amount. */
  classCodes: string[];
  /**
   * Opaque sha256 of the sorted asset ids in the population. Lets a refresh tell a changed
   * population from an unchanged one without storing ids. Absent on rows written before it.
   */
  populationFingerprint?: string;
  /**
   * Reviews that were set aside because the population changed after them, oldest first. The
   * task goes back to PENDING; what the accountant recorded for the earlier population is kept.
   */
  priorReviews?: IndefiniteLifeImpairmentPriorReview[];
  observedAt: string;
}

/** A DONE/WAIVED review of an earlier population, kept when the task is reopened. */
export interface IndefiniteLifeImpairmentPriorReview {
  status: 'DONE' | 'WAIVED';
  completedAt: string | null;
  completedByUserId: string | null;
  completionNotes: string | null;
  waivedAt: string | null;
  waivedByUserId: string | null;
  waivedReason: string | null;
  /** The population the review covered. Null only for a row written before counts were kept. */
  assetCount: number | null;
  classCodes: string[];
  reopenedAt: string;
}

export const PENDING_AUTO_REVERSE_SYSTEM_SOURCE = 'MANUAL_ENTRY_AUTOREVERSE_PENDING' as const;

export interface PendingAutoReverseSystemMetadata {
  version: 1;
  /** Every undrained promise targeting this period or earlier. Authoritative count. */
  pendingCount: number;
  /** Visibility-filtered manual-entry ids, so this may be shorter than `pendingCount`. */
  entryIds: string[];
  windowEnd: string;
  observedAt: string;
}

export interface ClosingPeriodTaskResponse {
  taskId: string;
  periodId: string;
  entityId: string;
  bookId: string;
  taskTemplateId: string | null;
  systemSource: string | null;
  systemMetadata: unknown;
  /**
   * Where the reader can go and act on this item, when the item knows (ALC-4242, S6 item 8).
   *
   * A system task computes its own link — the bank-reconciliation item points at the
   * reconciliation queue for its own period and entity — so the screen does not have to
   * re-derive which period the row is talking about. `null` for every task that has no such
   * place, which is most of them. Only ever a path plus ids the reader already holds.
   */
  actionHref: string | null;
  blocksClose: boolean;
  title: string;
  description: string;
  dueDayOfMonth: number | null;
  dueDate: string | null;
  section: ClosingChecklistSection | null;
  responsibleUserId: string | null;
  responsibleUserName: string | null;
  responsibleUserEmail: string | null;
  requireEvidence: boolean;
  status: ClosingTaskStatus;
  completedAt: string | null;
  completedByUserId: string | null;
  completionNotes: string | null;
  waivedAt: string | null;
  waivedByUserId: string | null;
  waivedReason: string | null;
  createdAt: string;
  updatedAt: string | null;
  attachments: ClosingTaskAttachmentResponse[];
}

export interface ListClosingPeriodTasksQuery {
  periodId: string;
  entityId: string;
  bookId: string;
}

export interface ListMyPendingClosingPeriodTasksQuery {
  limit?: number;
}

export interface UpdateClosingPeriodTaskStatusRequest {
  status: ClosingTaskStatus;
  observations?: string | null;
  waivedReason?: string | null;
}
